/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {catchError, exhaustMap, map, of, switchMap} from "rxjs";
import {concatLatestFrom} from "@ngrx/operators";
import {Store} from "@ngrx/store";
import {GeneralPartnerService} from "../../services/general-partner.service";
import {GeneralPartnerActions, GeneralPartnerListActions, GeneralPartnerOverviewActions} from "../fund.actions";
import {selectSelectedGeneralPartnerId, selectGeneralPartnerOverviewParams} from "./general-partner.selectors";
import {ErrorHandlingUtil} from "../../../shared/utils/error-handling-util";
import {Router} from "@angular/router";
import {MatSnackBar} from "@angular/material/snack-bar";
import {tap} from "rxjs/operators";

// #########################################
// General Partner OVERVIEW EFFECTS (server-side pagination)
// #########################################

export const triggerGeneralPartnerSearchOnParamsChange = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(
                GeneralPartnerOverviewActions.changePage,
                GeneralPartnerOverviewActions.changeSort,
                GeneralPartnerOverviewActions.changeSearch,
                GeneralPartnerOverviewActions.changeArchived
            ),
            map(() => GeneralPartnerOverviewActions.search())
        );
    },
    {functional: true}
);

export const searchGeneralPartners = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), gpService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerOverviewActions.search),
            concatLatestFrom(() => store$.select(selectGeneralPartnerOverviewParams)),
            switchMap(([, params]) =>
                gpService.searchGeneralPartners(params).pipe(
                    map((result) => GeneralPartnerOverviewActions.searchSuccess({result})),
                    catchError((errorResponse: any) => of(GeneralPartnerOverviewActions.searchError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadGeneralPartnerList = createEffect(
    (actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerListActions.load),
            exhaustMap(({archived}) =>
                generalPartnerService.getGeneralPartners(archived).pipe(
                    map((generalPartners) => GeneralPartnerListActions.loadSuccess({generalPartners})),
                    catchError((errorResponse: any) => of(GeneralPartnerListActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const loadGeneralPartner = createEffect(
    (actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.load),
            exhaustMap((action) => {
                return generalPartnerService.getGeneralPartner(action.generalPartnerId).pipe(
                    map((generalPartner) => GeneralPartnerActions.loadSuccess({generalPartner})),
                    catchError((errorResponse: any) => of(GeneralPartnerActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))));
            })
        );
    },
    {functional: true}
);

export const createGeneralPartner = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.create),
            exhaustMap((action) => {
                return generalPartnerService.saveGeneralPartner(action.generalPartner)
                    .pipe(
                        map((generalPartner) => GeneralPartnerActions.createSuccess({generalPartner})),
                        catchError((errorResponse: any) => of(GeneralPartnerActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))));
            })
        );
    },
    {functional: true}
);

export const saveGeneralPartner = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.save),
            exhaustMap((action) => {
                return generalPartnerService.saveGeneralPartner(action.generalPartner).pipe(
                    map((generalPartner) => generalPartner.id
                        ? GeneralPartnerActions.load({generalPartnerId: generalPartner.id})
                        : GeneralPartnerActions.loadError({errorMsg: "Error while saving the General Partner, please refresh the page."})),
                    catchError((errorResponse: any) => of(GeneralPartnerActions.loadError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))));
            })
        );
    },
    {functional: true}
);

// #########################################
// GENERAL PARTNER Archive
// #########################################
export const archiveGeneralPartner = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.archive),
            concatLatestFrom(() => store$.select(selectSelectedGeneralPartnerId)),
            exhaustMap(([action, partnerId]) => {
                if (partnerId) {
                    return generalPartnerService.archiveGeneralPartner(partnerId, action.archived).pipe(
                        map(generalPartner => GeneralPartnerActions.archiveSuccess({ generalPartner })),
                        catchError((errorResponse: any) => of(GeneralPartnerActions.archiveError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse),})))
                    );
                } else {
                    return of(GeneralPartnerActions.archiveError({errorMsg: "General partner could not be found",}));
                }
            })
        );
    },
    { functional: true }
);

export const showArchiveGeneralPartnerSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.archiveSuccess),
            tap((action) => {
                const archiveMsg = action.generalPartner.isArchived.value ? "archived." : "unarchived.";
                snackBar.open("General partner successfully "+archiveMsg, "", {
                    duration: 3000,
                });
            })
        );
    },
    { functional: true, dispatch: false }
);


export const createGeneralPartnerAndAssignToFund = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.createGeneralPartnerOnFund),
            exhaustMap((action) => {
                return generalPartnerService.createGeneralPartnerAndAssignToFund(action.generalPartner, action.fundId).pipe(
                    map((fund) => fund.generalPartner?.id
                        ? GeneralPartnerActions.createGeneralPartnerOnFundSuccess({fund})
                        : GeneralPartnerActions.createGeneralPartnerOnFundError({errorMsg: "Error while creating the General Partner, please refresh the page."})),
                    catchError((errorResponse: any) => of(GeneralPartnerActions.createGeneralPartnerOnFundError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))));
            })
        );
    },
    {functional: true}
);

export const saveGeneralPartnerOnFund = createEffect(
    (actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.saveGeneralPartnerOnFund),
            exhaustMap((action) => {
                return generalPartnerService.assignGeneralPartnerToFund(action.generalPartnerId, action.fundId).pipe(
                    map((fund) => fund.id
                        ? GeneralPartnerActions.saveGeneralPartnerOnFundSuccess({fund})
                        : GeneralPartnerActions.saveGeneralPartnerOnFundError({errorMsg: "Error while loading the fund, please refresh the page"})
                    ),
                    catchError((errorResponse: any) => of(GeneralPartnerActions.saveGeneralPartnerOnFundError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)}))));
            })
        );
    },
    {functional: true}
);

export const showGpAssignmentOnFundError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(
                GeneralPartnerActions.saveGeneralPartnerOnFundError,
                GeneralPartnerActions.createGeneralPartnerOnFundError
            ),
            tap((action) => {
                snackBar.open(action.errorMsg, "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const navigateAfterCreate = createEffect(
    (actions$ = inject(Actions), router$ = inject(Router)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.createSuccess),
            tap((action) => {
                router$.navigate(["/gp", action.generalPartner.id]);
            })
        );
    },
    {functional: true, dispatch: false}
);

export const loadFundsForGeneralPartner = createEffect(
    (store$ = inject(Store), actions$ = inject(Actions), generalPartnerService = inject(GeneralPartnerService)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.loadFunds),
            concatLatestFrom(() => store$.select(selectSelectedGeneralPartnerId)),
            exhaustMap(([_, generalPartnerId]) => {
                if (generalPartnerId) {
                    return generalPartnerService.getFundsForGeneralPartner(generalPartnerId).pipe(
                        map((funds) => GeneralPartnerActions.loadFundsSuccess({funds})),
                        catchError((errorResponse: any) => of(GeneralPartnerActions.loadFundsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse),})))
                    )
                } else {
                    return of(GeneralPartnerActions.loadFundsError({errorMsg: "No general partner selected"}));
                }

            })
        );
    },
    {functional: true}
);

export const loadFundsForGeneralPartnerOnGpLoaded = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(GeneralPartnerActions.loadSuccess),
            map(() => GeneralPartnerActions.loadFunds())
        );
    },
    { functional: true }
);

